import {SceneNodes} from "@cm/template-nodes"
import {ThreeObject, mathIsEqual, setThreeObjectPart, updateTransform} from "@template-editor/helpers/three-object"
import {Three as THREE} from "@cm/material-nodes/three"
import {toThreeMatrix} from "@template-editor/helpers/three-utils"
import {DisplayMode, ThreeSceneManagerService} from "@template-editor/services/three-scene-manager.service"
import {anyDifference, objectFieldsDifferent} from "@template-editor/helpers/change-detection"

export class ThreeRectangle extends ThreeObject<SceneNodes.Rectangle> {
    protected override renderObject: THREE.Mesh<THREE.BufferGeometry, THREE.Material>

    constructor(threeSceneManagerService: ThreeSceneManagerService, onAsyncUpdate: () => void) {
        super(threeSceneManagerService, onAsyncUpdate)
        const geometry = new THREE.PlaneGeometry()
        const material = new THREE.MeshBasicMaterial({color: 0xcccccc, side: THREE.DoubleSide, transparent: true, opacity: 0.5})
        this.renderObject = new THREE.Mesh(geometry, material)
        setThreeObjectPart(this.getRenderObject(), this)

        this.onDisplayModeChange(threeSceneManagerService.$displayMode())
    }

    override onDisplayModeChange(displayMode: DisplayMode) {
        this.renderObject.visible = displayMode === "editor"
    }

    override setup(sceneNode: SceneNodes.Rectangle) {
        return anyDifference([
            objectFieldsDifferent(
                sceneNode,
                this.parameters,
                ["transform", "width", "height"],
                (valueA, valueB) => {
                    if (typeof valueA === "object" && typeof valueB === "object") return mathIsEqual(valueA, valueB)
                    return valueA === valueB
                },
                ({transform, width, height}) => {
                    const threeMatrix = toThreeMatrix(transform)

                    const translation = new THREE.Vector3()
                    const rotation = new THREE.Quaternion()
                    const scale = new THREE.Vector3()
                    threeMatrix.decompose(translation, rotation, scale)

                    threeMatrix.compose(
                        translation,
                        rotation.multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(Math.PI / 2, 0, 0))),
                        scale.multiply(new THREE.Vector3(width, height, 1)),
                    )

                    updateTransform(threeMatrix, this.renderObject)
                },
            ),
        ])
    }

    override dispose() {
        this.renderObject.geometry.dispose()
        this.renderObject.material.dispose()
    }
}
